import { CalendarClock, Check, ChevronRight, FileText, Trash2 } from 'lucide-react'
import { languageInfo } from '../i18n/languages'
import type { LanguagePack } from '../i18n/packs'
import type { Copy } from '../i18n/translations'
import { formatDate } from '../services/explain'
import { relativeDays, trackedPapers } from '../services/tracker'
import type { Language, SavedDocument } from '../types/document'

export function SavedView({
  copy,
  p,
  language,
  items,
  onOpen,
  onDelete,
  onClear,
  onToggleDone
}: {
  copy: Copy
  p: LanguagePack
  language: Language
  items: SavedDocument[]
  onOpen: (item: SavedDocument) => void
  onDelete: (id: string) => void
  onClear: () => void
  onToggleDone: (item: SavedDocument) => void
}) {
  const tracked = trackedPapers(items)
  const bcp47 = languageInfo(language).bcp47
  const nameOf = (item: SavedDocument) => {
    const type = item.doc.documentType
    return type === 'unknown' ? p.docTypes.unknown.name : p.docTypes[type].name
  }
  return (
    <section className="history-view">
      <div className="page-title">
        <div>
          <p className="eyebrow">{copy.appName}</p>
          <h1>{copy.savedTitle}</h1>
        </div>
        {items.length > 0 && (
          <button className="text-button" onClick={onClear}>
            {copy.clearAll}
          </button>
        )}
      </div>
      <p className="field-hint">{copy.savedHint}</p>

      {tracked.length > 0 && (
        <section className="tracker" aria-label={copy.upcomingTitle}>
          <h2>
            <CalendarClock size={20} aria-hidden="true" /> {copy.upcomingTitle}
          </h2>
          <ol className="tracker-list">
            {tracked.map(({ item, days, done }) => (
              <li key={item.id} className={`tracker-item${done ? ' done' : days < 0 ? ' overdue' : days <= 7 ? ' soon' : ''}`}>
                <button className="tracker-open" onClick={() => onOpen(item)}>
                  <strong>{nameOf(item)}</strong>
                  <small>
                    📅 {formatDate(item.doc.deadline!, language)}
                    {!done && ` · ${relativeDays(days, bcp47)}`}
                  </small>
                </button>
                <button
                  className={done ? 'tracker-done is-done' : 'tracker-done'}
                  onClick={() => onToggleDone(item)}
                  aria-pressed={done}
                >
                  <Check size={18} aria-hidden="true" /> {done ? copy.doneLabel : copy.markDone}
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <FileText size={34} aria-hidden="true" />
          <p>{copy.emptySaved}</p>
        </div>
      ) : (
        <div className="history-list">
          {items.map((item) => {
            const name = nameOf(item)
            return (
              <div className="history-item" key={item.id}>
                <button className="history-open" onClick={() => onOpen(item)}>
                  <span className="history-icon">
                    <FileText size={20} aria-hidden="true" />
                  </span>
                  <span className="history-copy">
                    <strong>{name}</strong>
                    <small>
                      {new Date(item.savedAt).toLocaleDateString()}
                      {item.doc.deadline && ` · 📅 ${formatDate(item.doc.deadline, language)}`}
                    </small>
                  </span>
                  <ChevronRight size={19} className="chevron-icon" aria-hidden="true" />
                </button>
                <button className="history-delete-btn" onClick={() => onDelete(item.id)} aria-label={copy.deleteItem} title={copy.deleteItem}>
                  <Trash2 size={18} aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
